import React, { useState, useEffect, useCallback } from 'react';
import './Gallery.css';
import { useSiteData } from '../components/SiteData';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const CATEGORIES = [
  { value: '', label: 'All' },
  { value: 'academics', label: 'Academics' },
  { value: 'sports', label: 'Sports' },
  { value: 'events', label: 'Events' },
  { value: 'general', label: 'Campus Life' },
];

/* Assign bento span classes for visual interest */
const SPAN_PATTERN = ['feature', 'tall', '', 'wide', '', '', 'tall', '', 'wide', ''];

function GallerySkeleton() {
  return (
    <div className="gallery-bento" aria-busy="true" aria-label="Loading gallery photos">
      {Array.from({ length: 10 }).map((_, i) => (
        <div
          key={i}
          className={`gallery-item gallery-item--skeleton skeleton ${
            i === 0 ? 'gallery-item--feature' : i === 1 ? 'gallery-item--tall' : ''
          }`}
        />
      ))}
    </div>
  );
}

function Gallery() {
  const initialData = useSiteData();
  const [photos, setPhotos] = useState(initialData.gallery || []);
  const [loading, setLoading] = useState(!initialData.gallery);
  const [category, setCategory] = useState('');
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const qs = category ? `?category=${encodeURIComponent(category)}` : '';
    fetch(`${API_BASE}/gallery${qs}`)
      .then((r) => r.json())
      .then((d) => setPhotos(d.gallery || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, [category]);

  useEffect(() => { load(); }, [load]);

  /* Keyboard navigation */
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox((i) => Math.min(i + 1, photos.length - 1));
      if (e.key === 'ArrowLeft') setLightbox((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, photos.length]);

  /* Lock scroll */
  useEffect(() => {
    document.body.style.overflow = lightbox !== null ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [lightbox]);

  const spanClass = (idx) => {
    const p = SPAN_PATTERN[idx % SPAN_PATTERN.length];
    return p ? `gallery-item--${p}` : '';
  };

  return (
    <div className="gallery-page">

      {/* ── HEADER (no hero — template pattern) ─── */}
      <div className="gallery-page-header">
        <div className="gallery-header-inner">
          <div className="gallery-header-text">
            <h1>Memories &amp; Milestones</h1>
            <p>
              A living record of the moments, celebrations, and quiet everyday
              triumphs that make Twinkle Toes extraordinary.
            </p>
          </div>
          <div className="gallery-filters" role="group" aria-label="Filter by category">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                className={`gallery-filter-btn${category === cat.value ? ' gallery-filter-btn--active' : ''}`}
                onClick={() => setCategory(cat.value)}
                aria-pressed={category === cat.value}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── GRID ──────────────────────────────────── */}
      <div className="gallery-content">
        {loading ? (
          <GallerySkeleton />
        ) : photos.length === 0 ? (
          <div className="gallery-empty">
            <div className="gallery-empty-icon" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                <rect x="3" y="3" width="18" height="18" rx="3"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>
            <p>No photos in this category yet.</p>
            <button
              className="gallery-filter-btn gallery-filter-btn--active"
              onClick={() => setCategory('')}
            >
              View all photos
            </button>
          </div>
        ) : (
          <div className="gallery-bento">
            {photos.map((photo, idx) => (
              <button
                key={photo.id}
                className={`gallery-item ${spanClass(idx)}`}
                onClick={() => setLightbox(idx)}
                aria-label={`View: ${photo.caption || `Photo ${idx + 1}`}`}
              >
                <img
                  src={photo.image_url}
                  alt={photo.caption || `School photo ${idx + 1}`}
                  loading="lazy"
                />
                <div className="gallery-item-overlay" aria-hidden="true">
                  {photo.category && (
                    <span className="gallery-item-tag">{photo.category}</span>
                  )}
                  {photo.caption && (
                    <p className="gallery-item-caption">{photo.caption}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── LIGHTBOX ─────────────────────────────── */}
      {lightbox !== null && (
        <div
          className="lightbox-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
          onClick={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}
        >
          {/* Close */}
          <button className="lightbox-close" onClick={() => setLightbox(null)} aria-label="Close photo viewer">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          {/* Prev */}
          <button
            className="lightbox-nav lightbox-nav--prev"
            onClick={() => setLightbox((i) => Math.max(i - 1, 0))}
            disabled={lightbox === 0}
            aria-label="Previous photo"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>

          <div className="lightbox-center">
            <img
              className="lightbox-img"
              src={photos[lightbox].image_url}
              alt={photos[lightbox].caption || `Photo ${lightbox + 1}`}
            />
            {photos[lightbox].caption && (
              <p className="lightbox-caption">{photos[lightbox].caption}</p>
            )}
            <p className="lightbox-counter" aria-live="polite">
              {lightbox + 1} / {photos.length}
            </p>
          </div>

          {/* Next */}
          <button
            className="lightbox-nav lightbox-nav--next"
            onClick={() => setLightbox((i) => Math.min(i + 1, photos.length - 1))}
            disabled={lightbox === photos.length - 1}
            aria-label="Next photo"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default Gallery;
